import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { http } from "../lib/api";
import useCanonical from "../lib/useCanonical";
import { ChevronLeft } from "lucide-react";
import ArticleCard from "../components/ArticleCard";
import Newsletter from "../components/Newsletter";
import ShareButtons from "../components/ShareButtons";

export default function GlossaryTerm() {
  const { slug } = useParams();
  useCanonical(`/glossary/${slug}`);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    http
      .get(`/glossary/${slug}`, { validateStatus: (s) => s < 500 })
      .then((r) => {
        if (r.status === 404) setNotFound(true);
        else setData(r.data);
      })
      .finally(() => setLoading(false));
    // `http` is a stable module import; setters are stable React refs.
  }, [slug]);  

  // SEO + JSON-LD
  useEffect(() => {
    if (!data?.entry) return;
    const { entry } = data;
    const title = `${entry.term} — Tea Glossary | The Tea List`;
    const description = entry.short_def;
    document.title = title;
    const setMeta = (selector, attr, value) => {
      let el = document.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        const [, key, val] = selector.match(/\[([^=]+)="([^"]+)"\]/) || [];
        if (key && val) el.setAttribute(key, val);
        document.head.appendChild(el);
      }
      el.setAttribute(attr, value);
    };
    setMeta('meta[name="description"]', "content", description);
    setMeta('meta[property="og:title"]', "content", title);
    setMeta('meta[property="og:description"]', "content", description);
    setMeta('meta[property="og:type"]', "content", "article");

    const origin = window.location.origin;
    const url = `${origin}/glossary/${entry.slug}`;
    const ld = {
      "@context": "https://schema.org",
      "@type": "DefinedTerm",
      "@id": url,
      name: entry.term,
      alternateName: entry.pronunciation || undefined,
      description: entry.long_def,
      termCode: entry.slug,
      inDefinedTermSet: {
        "@type": "DefinedTermSet",
        name: "The Tea List Glossary",
        url: `${origin}/glossary`,
      },
    };
    let el = document.getElementById("ld-defined-term");
    if (!el) {
      el = document.createElement("script");
      el.type = "application/ld+json";
      el.id = "ld-defined-term";
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(ld);

    return () => {
      document.title = "The Tea List — Australia's independent tea journal";
      document.getElementById("ld-defined-term")?.remove();
    };
    // setMeta + ld payload are derived from `data` inside the effect.
  }, [data]);  

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-6 md:px-12 py-32 text-center">
        <p className="font-mono text-[11px] tracking-widest uppercase text-[#66726d] animate-pulse">
          steeping…
        </p>
      </div>
    );
  }

  if (notFound || !data?.entry) {
    return (
      <div className="max-w-3xl mx-auto px-6 md:px-12 py-32 text-center">
        <h2 className="font-serif text-4xl">Term not found.</h2>
        <Link to="/glossary" className="inline-block mt-6 btn-secondary">
          Back to the Glossary
        </Link>
      </div>
    );
  }

  const { entry, related_terms = [], related_posts = [] } = data;
  const url =
    typeof window !== "undefined" ? `${window.location.origin}/glossary/${entry.slug}` : "";

  return (
    <div className="relative z-10" data-testid="glossary-term-page">
      <article className="max-w-4xl mx-auto px-6 md:px-12 pt-10 md:pt-14">
        <Link
          to="/glossary"
          className="inline-flex items-center gap-1 font-mono text-[10px] tracking-widest uppercase text-[#66726d] hover:text-[#1e4a35]"
          data-testid="glossary-back-link"
        >
          <ChevronLeft size={12} /> Glossary
        </Link>

        <p className="overline mt-6 mb-3">{entry.category}</p>
        <h1 className="headline-xl text-5xl md:text-7xl">{entry.term}</h1>
        {entry.pronunciation && (
          <p
            className="mt-3 font-mono text-sm tracking-widest text-[#66726d]"
            data-testid="glossary-pronunciation"
          >
            /{entry.pronunciation}/
          </p>
        )}

        <p
          className="mt-10 text-2xl md:text-3xl font-light italic text-[#404e48] leading-snug max-w-3xl"
          data-testid="glossary-short-def"
        >
          {entry.short_def}
        </p>

        <div
          className="mt-12 prose-article max-w-2xl text-lg leading-relaxed"
          data-testid="glossary-long-def"
        >
          {entry.long_def.split("\n\n").map((para, i) => (
            <p key={`${entry.slug}-p-${i}`}>{para}</p>
          ))}
        </div>

        <div
          className="mt-12 pt-8 border-t"
          style={{ borderColor: "var(--border)" }}
        >
          <ShareButtons title={`${entry.term} — Tea Glossary`} url={url} />
        </div>
      </article>

      {related_terms.length > 0 && (
        <section
          className="mt-16 py-12 md:py-16 px-6 md:px-12 border-y"
          style={{ borderColor: "var(--border)", backgroundColor: "var(--bg-alt)" }}
        >
          <div className="max-w-4xl mx-auto">
            <p className="overline mb-3">Related terms</p>
            <h2 className="font-serif text-3xl md:text-4xl font-light tracking-tight mb-8">
              Words that travel together.
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {related_terms.map((t) => (
                <Link
                  key={t.slug}
                  to={`/glossary/${t.slug}`}
                  className="group block border-l-2 pl-5 py-1 hover:border-[#1e4a35] transition-colors"
                  style={{ borderColor: "var(--border)" }}
                  data-testid={`related-term-${t.slug}`}
                >
                  <h3 className="font-serif text-xl font-medium group-hover:text-[#1e4a35]">
                    {t.term}
                  </h3>
                  <p className="mt-2 text-sm text-[#404e48] font-light leading-relaxed">
                    {t.short_def}
                  </p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {related_posts.length > 0 && (
        <section className="py-12 md:py-16 px-6 md:px-12">
          <div className="max-w-[1400px] mx-auto">
            <p className="overline mb-3">From the Journal</p>
            <h2 className="font-serif text-3xl md:text-4xl font-light tracking-tight mb-10">
              Read further.
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
              {related_posts.map((p) => (
                <ArticleCard key={p.slug} post={p} />
              ))}
            </div>
          </div>
        </section>
      )}

      <Newsletter />
    </div>
  );
}
